/**
 * The topbar's breadcrumb trail (Figma `186:6609`, Topbar `Kind=Breadcrumb`),
 * and the one way a page names what it shows in it.
 */

import { Link } from '@tanstack/react-router';
import { createContext, Fragment, useContext, useEffect, type ReactNode } from 'react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../ui/breadcrumb';

export interface Crumb {
  label: string;
  href?: string;
}

export interface Leaf {
  label: string;
  /** Renames the crumb above a page that sits below its entry's own route --
   *  the tracking editor names the account it configures, where the "Conta"
   *  entry alone would not. Unread on a route its entry names exactly. */
  ancestor?: string;
}

const LeafContext = createContext<((leaf: Leaf | null) => void) | null>(null);

export const LeafProvider = LeafContext.Provider;

/**
 * Names the thing the page shows -- a post's title, an account's name -- for
 * the trail's last crumb. On a route its nav entry names exactly it replaces
 * that entry's generic label ("Publicação"); on a route below its entry
 * (an account's tracking editor under "Conta") it becomes a crumb of its own.
 * `undefined` while the page's data is still loading leaves the trail as is.
 */
export function useBreadcrumbLeaf(label: string | undefined, ancestor?: string): void {
  const setLeaf = useContext(LeafContext);
  useEffect(() => {
    if (setLeaf === null || label === undefined) return;
    setLeaf(ancestor === undefined ? { label } : { label, ancestor });
    return () => setLeaf(null);
  }, [setLeaf, label, ancestor]);
}

/** `exact`: the last of `crumbs` is the route on screen, not an ancestor of it. */
export function applyLeaf(crumbs: readonly Crumb[], exact: boolean, leaf: Leaf | null): Crumb[] {
  if (leaf === null) return [...crumbs];
  if (exact) return [...crumbs.slice(0, -1), { label: leaf.label }];
  const above = crumbs.at(-1);
  const ancestors =
    above !== undefined && leaf.ancestor !== undefined
      ? [...crumbs.slice(0, -1), { ...above, label: leaf.ancestor }]
      : crumbs;
  return [...ancestors, { label: leaf.label }];
}

/**
 * Below 1024 only the last crumb shows, the Mobile variant's lone title
 * (Figma `123:189`); every crumb stays in the one `nav` so the accessibility
 * tree reads the same trail at every width.
 */
export function Trail(props: { crumbs: readonly Crumb[] }): ReactNode {
  const last = props.crumbs.length - 1;
  if (last < 0) return null;
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="text-body-sm flex-nowrap gap-2 sm:gap-2">
        {props.crumbs.map((crumb, index) => {
          const ancestor = index < last;
          return (
            <Fragment key={`${index}-${crumb.label}`}>
              {index > 0 && <BreadcrumbSeparator className="hidden lg:inline-flex" />}
              <BreadcrumbItem className={ancestor ? 'hidden shrink-0 lg:inline-flex' : 'min-w-0'}>
                {!ancestor ? (
                  <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                ) : crumb.href !== undefined ? (
                  <BreadcrumbLink asChild>
                    {/* `exact`: a fuzzy-active `Link` marks itself
                     * `aria-current="page"`, and every ancestor's path is a
                     * prefix of the page's own. */}
                    <Link to={crumb.href} activeOptions={{ exact: true }}>
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
